<template>
  <div>
    <label class="grey--text text--darken-4 text-body-2 font-weight-medium">
      {{ title }}
    </label>

    <div
      @click="$refs.fileInput.click()"
      @dragleave.prevent="onDragLeave"
      @dragover.prevent="onDragOver"
      @drop.prevent="onDrop"
      class="mt-1 upload-area"
      :class="{ 'is-dragover': isDragging }"
    >
      <v-icon size="64" color="primary">mdi-cloud-upload</v-icon>
      <div class="text-h6 mt-2">
        Arraste arquivos aqui ou
        <span class="primary--text">clique para selecionar</span>
      </div>

      <div
        v-if="filesTypesText"
        class="text-caption grey--text text--darken-1 mb-2"
      >
        {{ filesTypesText }}
      </div>

      <input
        type="file"
        ref="fileInput"
        :accept="accept"
        class="d-none"
        multiple
        @change="handleFiles"
      />
    </div>

    <div
      v-if="helper"
      class="grey--text text-caption font-weight-regular ml-2 mt-1"
    >
      {{ helper }}
    </div>

    <template v-if="files.length > 0">
      <div class="black--text text-body-2 font-weight-medium mt-4">
        {{ titleUpload }}
      </div>

      <v-list class="mt-2">
        <v-list-item
          class="list-item-file"
          v-for="(file, index) in files"
          :key="index"
        >
          <v-list-item-icon>
            <v-icon>mdi-file-document-outline</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ file.name }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ (file.size / 1024).toFixed(2) }} KB
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-btn @click.stop="removeFile(index)" icon>
              <v-icon color="red">mdi-delete-outline</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </template>

    <v-divider v-if="files.length > 0 && items.length > 0" class="my-2" />

    <template v-if="items.length > 0">
      <div class="d-flex align-center justify-space-between mt-4">
        <div class="black--text text-body-2 font-weight-medium">
          {{ titleUploaded }}
        </div>

        <div class="d-flex">
          <v-btn
            @click.prevent="checkAllUploaded()"
            color="#dbdee1"
            outlined
            small
          >
            <div class="black--text text-none">Selecionar tudo</div>
          </v-btn>

          <v-btn
            @click.prevent="toggleDelete"
            :disabled="!selectedUploadedFiles.length"
            class="ml-2"
            color="#e7000b"
            depressed
            small
          >
            <div class="white--text text-none">
              Excluir Selecionados
              {{
                selectedUploadedFiles.length > 0
                  ? `(${selectedUploadedFiles.length})`
                  : ""
              }}
            </div>
          </v-btn>
        </div>
      </div>

      <div class="mt-3 grid-list-files">
        <FileCardFarm
          v-for="(item, key) in items"
          @select="selectUploadedFile(item.id)"
          @delete="
            selectUploadedFile(item.id);
            toggleDelete();
          "
          :key="key + '-uploaded'"
          v-bind="item"
          :checked="selectedUploadedFiles.includes(item.id)"
        />
      </div>
    </template>

    <v-dialog
      v-if="selectedUploadedFiles.length > 0 && dialogDelete"
      v-model="dialogDelete"
      max-width="500"
      persistent
    >
      <DeleteItemDialog
        @confirm="onDelete"
        @close="toggleDelete()"
        :loading="isLoadingDelete"
        :title="
          selectedUploadedFiles.length > 0
            ? 'Apagar arquivos'
            : 'Apagar arquivo'
        "
        :name="
          selectedUploadedFiles.length > 1
            ? selectedUploadedFiles.length + ' arquivos selecionados'
            : 'esse arquivo'
        "
      />
    </v-dialog>
  </div>
</template>

<script>
import FileCardFarm from "@/components/cards/farm/File.vue";
import DeleteItemDialog from "@/components/dialogs/DeleteItem.vue";
import formatDate from "@/utils/formatDate";
import { mapActions } from "vuex";

import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default {
  components: { FileCardFarm, DeleteItemDialog },
  props: {
    title: String,
    table: String,
    accept: {
      type: String,
      default: "*",
    },
    helper: {
      type: String,
      default: "",
    },
    items: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    isDragging: false,
    isLoadingDelete: false,
    files: [],
    item: null,
    selectedUploadedFiles: [],
    dialogDelete: false,
    dbPromise: null,
  }),
  computed: {
    filesTypesText() {
      if (this.accept === "*") return "Suporta todos os formatos";

      const exts = this.accept
        .split(",")
        .map((ext) => ext.trim().replace(".", "").toUpperCase());

      if (exts.length === 1) {
        return `Suporta arquivos ${exts[0]}`;
      }

      const last = exts.pop();
      return `Suporta arquivos ${exts.join(", ")} e ${last}`;
    },
    titleUpload() {
      return (
        "Arquivos selecionados" +
        (this.files?.length > 0 ? ` (${this.files.length})` : "")
      );
    },
    titleUploaded() {
      return (
        "Arquivos enviados" +
        (this.items?.length > 0 ? ` (${this.items.length})` : "")
      );
    },
  },
  methods: {
    ...mapActions("properties", ["destroyFiles"]),
    parseSelectedFiles() {
      return this.items.filter(({ id }) =>
        this.selectedUploadedFiles.includes(id),
      );
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(e) {
      this.isDragging = false;
      this.validateAndAddFiles(e.dataTransfer.files);
    },
    handleFiles(e) {
      this.validateAndAddFiles(e.target.files);
      e.target.value = "";
    },
    validateAndAddFiles(fileList) {
      const newFiles = Array.from(fileList);
      const allowedExtensions = this.accept
        .split(",")
        .map((ext) => ext.trim().toLowerCase());

      const validFiles = [];
      let hasInvalid = false;

      newFiles.forEach((file) => {
        const fileName = file.name.toLowerCase();
        const isValid = allowedExtensions.some((ext) => {
          if (ext === "*") return true;
          return fileName.endsWith(ext);
        });

        if (isValid) {
          validFiles.push(file);
        } else {
          hasInvalid = true;
        }
      });

      if (hasInvalid) {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-file-alert",
          message:
            "Alguns arquivos não são suportados. Verifique e tente novamente.",
          type: "alert",
        });
      }

      if (validFiles.length > 0) {
        this.files = [...this.files, ...validFiles];
        this.$emit("input", this.files);
      }
    },
    checkAllUploaded() {
      if (this.selectedUploadedFiles.length === this.items.length) {
        this.selectedUploadedFiles = [];
      } else {
        this.selectedUploadedFiles = this.items.map(({ id }) => {
          return id;
        });
      }
    },
    async onDelete() {
      if (
        !this.selectedUploadedFiles ||
        this.selectedUploadedFiles.length === 0
      )
        return;

      this.isLoadingDelete = true;
      const files = [...this.selectedUploadedFiles]; // Cópia do array

      try {
        const params = {
          type: this.table,
          ids: files,
        };

        const r = await this.destroyFiles(params);

        if (r) {
          const db = await this.dbPromise;
          if (db) {
            await Promise.all(files.map((id) => db.delete("thumbnails", id)));
          }

          this.$eventHub.$emit("snackBar", {
            icon: "mdi-check-circle",
            message: `${files.length} arquivos excluídos com sucesso.`,
            type: "success",
          });

          this.$emit("delete-files", params);

          this.selectedUploadedFiles = [];
          this.dialogDelete = false;
        }
      } catch (error) {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-delete-alert",
          message: "Erro ao excluir os arquivos selecionados. Tente novamente.",
          type: "error",
        });
      } finally {
        this.isLoadingDelete = false;
      }
    },
    removeFile(index) {
      this.files.splice(index, 1);
      this.$emit("input", this.files);
    },
    toggleDelete() {
      this.dialogDelete = !this.dialogDelete;
    },
    selectUploadedFile(id) {
      if (!this.selectedUploadedFiles.includes(id)) {
        this.selectedUploadedFiles.push(id);
      } else {
        this.selectedUploadedFiles = this.selectedUploadedFiles.filter(
          (item) => item !== id,
        );
      }
    },
    formatDate,
  },
};
</script>

<style lang="scss" scoped>
.upload-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 18px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  background-color: #fafafa;

  &:hover,
  &.is-dragover {
    border-color: #1976d2;
    background-color: #e3f2fd;
  }
}

.d-none {
  display: none;
}

.list-item-file {
  background-color: transparent;
  border-radius: 8px;
  transition: all 0.1s linear;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

.grid-list-files {
  $columns: 4;
  $gap: 12px;

  display: grid;
  gap: $gap;
  grid-template-columns: repeat(
    $columns,
    calc((100% / $columns) - ($gap * ($columns - 1) / $columns))
  );
}
</style>
